import {
  useState,
  useRef,
  useLayoutEffect,
  useId,
  ChangeEvent,
  KeyboardEvent,
  MouseEventHandler,
} from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton } from '@mui/material';
import { useChatSurfaceContext } from '../../context/chat-surface-context';
import './styles.css';

export interface ChatMessageReviewBackdropProps {
  onClick: MouseEventHandler<HTMLElement>;
  open: boolean;
}

export function ChatMessageReviewBackdrop({
  onClick,
  open,
}: ChatMessageReviewBackdropProps) {
  return (
    // Decorative click-to-close overlay for mouse users; keyboard users dismiss
    // the dialog via Escape or its close button, so no key handler is needed here.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      onClick={onClick}
      className={`cs-review-backdrop ${
        open ? 'cs-review-backdrop--visible' : 'cs-review-backdrop--hidden'
      }`}
    />
  );
}

export interface ChatMessageReviewProps {
  closeAction: () => void;
  submitAction: (comment: string) => void;
  /** Override the default title text. */
  title?: string;
  /** Override the default textarea placeholder. */
  placeholder?: string;
  /** Override the default submit button label. */
  submitLabel?: string;
}

export function ChatMessageReview({
  closeAction,
  submitAction,
  title = "Why wasn't this helpful?",
  placeholder = 'Add more details here',
  submitLabel = 'Submit',
}: ChatMessageReviewProps) {
  const { icons } = useChatSurfaceContext();
  const CloseIcon = icons.Close;
  const [comment, setComment] = useState('');
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // On open, isolate the background from assistive tech and move focus to the
  // dialog so VoiceOver announces it.
  //
  // Announcement: focus the role="dialog" CONTAINER (not a child control).
  // VoiceOver announces the dialog — "<title>, dialog, N items" — when its
  // reading cursor lands on the dialog element, so focusing the container is
  // what makes it announce on open. (A synthetic aria-live announcement was
  // tried and proved unreliable here — dropped during the large aria-hidden
  // re-scan below — and is redundant with VO's native dialog announcement.)
  // The focus is deferred one frame so it runs after the isolation has settled
  // and the portaled dialog is in the a11y tree; focusing synchronously left
  // VO's cursor outside the dialog. Restoring focus to the trigger on close is
  // owned by the consumer (the thumbs-down button blurs to <body> on click, so
  // this component can't read it here) — WCAG 2.4.3.
  //
  // Isolation: aria-modal="true" alone is NOT honored by VoiceOver. Because the
  // dialog is portaled to <body>, hiding the dialog's siblings hides <body>'s
  // other children (#root + scripts) — so <main> (inside #root) is gone and the
  // dialog is the only reachable content, which is what stops the VO cursor
  // landing on "empty main" (WCAG 4.1.2 / 1.3.1). aria-hidden rather than
  // `inert` so the backdrop's click-to-close still works for mouse users.
  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const hidden: Element[] = [];
    let node: Element = dialog;
    while (node.parentElement && node !== document.body) {
      const parent = node.parentElement;
      for (const sibling of Array.from(parent.children)) {
        if (sibling !== node && sibling.getAttribute('aria-hidden') !== 'true') {
          sibling.setAttribute('aria-hidden', 'true');
          hidden.push(sibling);
        }
      }
      node = parent;
    }

    const raf = requestAnimationFrame(() => dialog.focus());

    return () => {
      cancelAnimationFrame(raf);
      hidden.forEach((el) => el.removeAttribute('aria-hidden'));
    };
  }, []);

  const handleTextChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setComment(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      closeAction();
      return;
    }
    // Keep Tab focus inside the dialog while it's open (WCAG 2.4.3): wrap from
    // the last focusable back to the first and vice-versa.
    if (event.key === 'Tab' && dialogRef.current) {
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const handleSubmit = () => {
    submitAction(comment);
    closeAction();
  };

  // Portal to <body> so the dialog is a top-level modal rather than an inline
  // panel buried inside <main>: that inline nesting is what left VoiceOver's
  // reading cursor parked on "empty main" on open. At body level the isolation
  // above hides everything else and the dialog is the only reachable content.
  return createPortal(
    // onKeyDown implements the modal's Escape-to-close and Tab focus-trap on the
    // dialog container — the standard dialog pattern, VoiceOver-verified (Flow G).
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={dialogRef}
      className="cs-review"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <IconButton
        className="cs-review-close-button"
        aria-label="Close"
        onClick={closeAction}
      >
        <CloseIcon />
      </IconButton>
      <div className="cs-review-title">
        <span id={titleId} className="cs-review-title__text">
          {title}
        </span>
      </div>
      <div className="cs-review-content">
        <textarea
          ref={textareaRef}
          onChange={handleTextChange}
          placeholder={placeholder}
          aria-label={title}
          value={comment}
        />
      </div>
      <div className="cs-review-action">
        <Button
          className="primary-button"
          variant="outlined"
          onClick={handleSubmit}
          disabled={!comment}
        >
          {submitLabel}
        </Button>
      </div>
    </div>,
    document.body
  );
}
